/**
 * Dice notation parser and roller for the session HUD (#257 / 13.9.1).
 *
 * Pure functions only — no side effects, no DOM access. All randomness is
 * injected via an optional `rng` parameter (default `Math.random`), enabling
 * deterministic test coverage with a seed function.
 *
 * Supported notation subset (Phase 2):
 *   NdM        — basic: `1d20`, `2d6`, `1d4`
 *   NdM+K      — with bonus: `2d6+3`, `1d20-2`
 *   NdM-K      — with penalty: `1d8-1`
 *   NdMkhN     — keep highest N: `4d6kh3` (drop-lowest for stat arrays)
 *   NdMklN     — keep lowest N: `4d6kl1`
 *
 * `N` (count) and `M` (sides) must be ≥ 1. Modifier K is an integer (may be
 * negative; applied AFTER keeping). Keep-count must be ≥ 1 and ≤ N.
 *
 * Design note: keeping strictly in the client bundle (no worker, no shared
 * package) because dice rolling is pure UI bookkeeping — ephemeral session
 * state that never persists to the op log.
 */

/** An error thrown when the input notation is malformed. */
export class DiceParseError extends Error {
  constructor(
    public readonly notation: string,
    message: string,
  ) {
    super(`Invalid dice notation "${notation}": ${message}`);
    this.name = 'DiceParseError';
  }
}

/** The components of a parsed dice expression. */
export interface DiceExpr {
  /** Number of dice to roll. */
  count: number;
  /** Number of sides per die. */
  sides: number;
  /** Optional keep-highest or keep-lowest directive. */
  keep?: { mode: 'highest' | 'lowest'; n: number };
  /** Flat integer modifier applied after keeping. */
  modifier: number;
}

/** Result of rolling one die expression. */
export interface DiceRoll {
  /** The original notation string. */
  notation: string;
  /** The parsed expression. */
  expr: DiceExpr;
  /** Per-die outcomes, in roll order (before any keep filter). */
  dice: readonly number[];
  /** Which indices of `dice` were kept (sorted ascending). */
  keptIndices: readonly number[];
  /** Sum of kept dice + modifier. */
  total: number;
}

/**
 * Parse a dice notation string into a `DiceExpr`.
 * Throws `DiceParseError` on invalid input.
 *
 * The parser is intentionally strict — unknown suffixes are rejected so
 * the GM gets an actionable error rather than a silently wrong result.
 */
export function parseDice(notation: string): DiceExpr {
  const raw = notation.trim().toLowerCase();
  if (!raw) throw new DiceParseError(notation, 'empty input');

  // Pattern: <count>d<sides>[kh<n>|kl<n>][+/-<modifier>]
  //          count defaults to 1 when omitted (e.g. "d20")
  const RE =
    /^(\d+)?d(\d+)(?:(kh|kl)(\d+))?(?:([+-])(\d+))?$/;
  const m = RE.exec(raw);
  if (!m) throw new DiceParseError(notation, 'unrecognised format — expected e.g. 2d6+3 or 4d6kh3');

  const count = m[1] !== undefined ? parseInt(m[1], 10) : 1;
  const sides = parseInt(m[2]!, 10);
  const keepMode = m[3] as 'kh' | 'kl' | undefined;
  const keepN = m[4] !== undefined ? parseInt(m[4], 10) : undefined;
  const modSign = m[5] as '+' | '-' | undefined;
  const modAbs = m[6] !== undefined ? parseInt(m[6], 10) : 0;
  const modifier = modSign === '-' ? -modAbs : modAbs;

  if (count < 1) throw new DiceParseError(notation, 'die count must be ≥ 1');
  if (count > 1000) throw new DiceParseError(notation, 'die count must be ≤ 1000');
  if (sides < 1) throw new DiceParseError(notation, 'die sides must be ≥ 1');
  if (sides > 10000) throw new DiceParseError(notation, 'die sides must be ≤ 10000');

  let keep: DiceExpr['keep'];
  if (keepMode !== undefined && keepN !== undefined) {
    if (keepN < 1) throw new DiceParseError(notation, 'keep count must be ≥ 1');
    if (keepN > count) throw new DiceParseError(notation, `keep count (${keepN}) must be ≤ die count (${count})`);
    keep = { mode: keepMode === 'kh' ? 'highest' : 'lowest', n: keepN };
  }

  return keep !== undefined ? { count, sides, modifier, keep } : { count, sides, modifier };
}

/**
 * Roll a parsed `DiceExpr`, returning a full `DiceRoll` with per-die values
 * and the final total.
 *
 * @param expr     The parsed expression.
 * @param notation The original notation string (stored verbatim in the result).
 * @param rng      Random number source in [0,1). Defaults to `Math.random`.
 *                 Pass a seeded function for deterministic tests.
 */
export function rollDice(
  expr: DiceExpr,
  notation: string,
  rng: () => number = Math.random,
): DiceRoll {
  const dice: number[] = [];
  for (let i = 0; i < expr.count; i++) {
    dice.push(Math.floor(rng() * expr.sides) + 1);
  }

  // Determine kept indices.
  let keptIndices: number[];
  if (expr.keep) {
    const { mode, n } = expr.keep;
    // Sort indices by die value.
    const sorted = dice
      .map((v, i) => ({ v, i }))
      .sort((a, b) => a.v - b.v);
    const kept = mode === 'highest' ? sorted.slice(-n) : sorted.slice(0, n);
    keptIndices = kept.map((x) => x.i).sort((a, b) => a - b);
  } else {
    keptIndices = dice.map((_, i) => i);
  }

  const total =
    keptIndices.reduce((sum, idx) => sum + (dice[idx] ?? 0), 0) + expr.modifier;

  return { notation, expr, dice, keptIndices, total };
}

/**
 * Convenience: parse + roll in one step.
 * Throws `DiceParseError` if the notation is invalid.
 */
export function roll(
  notation: string,
  rng: () => number = Math.random,
): DiceRoll {
  const expr = parseDice(notation);
  return rollDice(expr, notation, rng);
}

/**
 * Human-readable breakdown for a completed `DiceRoll`.
 * Formats as: `[kept, dropped] + modifier = total`
 * e.g. `[5, 3, 2] (dropped 1) + 3 = 10`
 */
export function rollBreakdown(r: DiceRoll): string {
  const keptSet = new Set(r.keptIndices);
  const keptValues = r.keptIndices.map((i) => r.dice[i] ?? 0);
  const droppedValues = r.dice.filter((_, i) => !keptSet.has(i));

  const diceStr = `[${keptValues.join(', ')}]`;
  const droppedStr = droppedValues.length > 0 ? ` (dropped ${droppedValues.join(', ')})` : '';
  const modStr =
    r.expr.modifier > 0
      ? ` + ${r.expr.modifier}`
      : r.expr.modifier < 0
        ? ` − ${Math.abs(r.expr.modifier)}`
        : '';

  return `${diceStr}${droppedStr}${modStr} = ${r.total}`;
}
